import { Edge, Node } from '@xyflow/react';
import { IVlanItem } from 'src/context/global/types';
import {
  EDataType,
  NodeCategoryModel,
  IDhcpServerSettings,
} from 'src/modules/network-configuration/models/node-data';
import {
  IConfigurationDhcp,
  IConfigurationLease,
  IConfigurationSubnet,
} from 'src/types/configuration';

import { getConnectPortFromEdge } from '../get-connect-port-from-edge';
import {
  isValidIPAddress,
  calculateNetworkAddress,
  convertSubnetMaskToLength,
} from '../ip';
import {
  getEdgeConnected,
  getAllNodeConnected,
  getPortIndexConnected,
} from '../node';

const isDeviceDhcpEnabled = (node: Node): boolean => {
  const category = node.data?.category as NodeCategoryModel;
  const ipInterfaces = category?.ipInterfaces;

  if (ipInterfaces && ipInterfaces.length > 0) {
    return ipInterfaces.some((ip) => !!ip.dhcp);
  }

  return !!category?.dhcp;
};

export const configurationDhcp = ({
  nodeId,
  nodeCategory,
  edges,
  nodes,
  vlans,
  isAutoDeploy,
  autoDeployIps,
}: {
  nodeId: string;
  nodeCategory: NodeCategoryModel;
  edges: Edge[];
  nodes: Node[];
  vlans: IVlanItem[];
  isAutoDeploy: boolean;
  autoDeployIps: {
    id: string;
    name: string;
    ip: string;
  }[];
}): IConfigurationDhcp => {
  const dhcp: IConfigurationDhcp = {
    lease: [],
    subnets: [],
    version: 3,
    vids: [],
  };

  // get all edges connected to the node
  const connectedEdges = edges.filter(
    (edge) => edge.source === nodeId || edge.target === nodeId,
  );

  // get all nodes connected to the node
  const connectedNodes = nodes.filter((node) =>
    connectedEdges.some(
      (edge) =>
        (edge.target === nodeId && edge.source === node.id) ||
        (edge.source === nodeId && edge.target === node.id),
    ),
  );

  // all devices connected to the node
  const connectedDevices = connectedNodes.filter(
    (node) =>
      (node.data?.category as NodeCategoryModel)?.type === EDataType.Device,
  );

  // all node has dhcp server
  const nodesHasDhcp = connectedDevices.filter(isDeviceDhcpEnabled);
  const leasesVlanId = [];
  // ----
  const leases: IConfigurationLease[] = [];
  for (let i = 0; i < nodesHasDhcp.length; i += 1) {
    const targetNode = nodesHasDhcp[i];
    const targetNodeCategory = targetNode.data?.category as NodeCategoryModel;
    const edge = getEdgeConnected(nodeId, targetNode.id, edges);
    let portIndexValue = 0;
    if (edge) {
      const targetPort = getConnectPortFromEdge(edge, nodeId);
      const { portIndexes } = nodeCategory;
      const portIndex = portIndexes[targetPort] || 0;
      portIndexValue = portIndex - 1;
    }
    const vlansEdge = edge ? (edge.data as any)?.vlanIds || [] : [];
    if (vlansEdge.length > 0) {
      leasesVlanId.push(...vlansEdge.map((id: string) => parseInt(id, 10)));
      vlansEdge.forEach((vlanId: string) => {
        const ipInterface = targetNodeCategory.ipInterfaces?.find(
          (item) =>
            parseInt(item.vlanId.toString(), 10) === parseInt(vlanId, 10),
        );
        leases.push({
          ip: ipInterface?.ip || '',
          options: [],
          type: 0,
          value: [parseInt(vlanId, 10), portIndexValue],
        });
      });
    } else {
      leases.push({
        ip: targetNodeCategory.ip || '',
        options: [],
        type: 0,
        value: [0, portIndexValue],
      });
    }
  }

  // Sort by value[0] and value[1]
  leases.sort((a, b) => {
    if (a.value[0] === b.value[0]) {
      return a.value[1] - b.value[1];
    }
    return a.value[0] - b.value[0];
  });

  dhcp.lease = leases;

  const defaultVid = {
    type: 'off',
    vid: 0,
  };

  const defaultSubnet = {
    ip: '',
    leasetime: 86400,
    mask: '',
    options: [],
    pool_end: '0.0.0.0',
    pool_start: '0.0.0.0',
  };
  const uniqueLeasesVlanId = [...new Set(leasesVlanId)];
  if (nodeCategory?.ipInterfaces && nodeCategory?.ipInterfaces?.length > 0) {
    nodeCategory?.ipInterfaces.forEach((ipInterface) => {
      // Check if pool is enabled
      const isPoolEnabled = ipInterface?.dhcpServer?.poolEnabled || false;

      // Check if at least one peripheral device connected to this switch has "port based DHCP" enabled
      const hasPortBasedDhcp = uniqueLeasesVlanId.includes(
        parseInt(ipInterface?.vlanId.toString(), 10),
      );

      // Server is enabled if pool is enabled OR port-based DHCP is enabled
      const isServerEnabled = isPoolEnabled || hasPortBasedDhcp;

      // Only include subnet when needed: pool enabled or port-based DHCP lease for this VLAN
      if (isServerEnabled) {
        dhcp.subnets.push(
          getDhcpSubnet(
            ipInterface?.subnet || '',
            ipInterface?.subnetMask || '',
            ipInterface?.dhcpServer,
          ),
        );
      }

      dhcp.vids.push({
        type: isServerEnabled ? 'server' : 'off',
        vid: parseInt(ipInterface?.vlanId.toString(), 10) || 0,
      });
    });
  } else {
    const subnetMask = nodeCategory.subnetMask || '255.255.255.0';
    const subnetMaskLength = convertSubnetMaskToLength(subnetMask);
    const subnetIp = calculateNetworkAddress(
      nodeCategory.ip || '',
      subnetMaskLength,
    );

    // Check if pool is enabled
    const isPoolEnabled = nodeCategory.dhcpServer?.poolEnabled || false;

    // Check if at least one peripheral device connected to this switch has "port based DHCP" enabled
    const isSomeDeviceDhcp = connectedDevices.some(isDeviceDhcpEnabled);

    // Server is enabled if pool is enabled OR port-based DHCP is enabled
    const isServerEnabled = isPoolEnabled || isSomeDeviceDhcp;

    // Only include subnet when needed: pool enabled or port-based DHCP lease
    if (isServerEnabled) {
      dhcp.subnets.push(
        getDhcpSubnet(subnetIp, subnetMask, nodeCategory.dhcpServer),
      );
    }

    defaultVid.type = isServerEnabled ? 'server' : 'off';
    dhcp.vids.push(defaultVid);
  }

  // auto deploy
  if (isAutoDeploy) {
    dhcp.vids.push({
      type: 'server',
      vid: 3579,
    });
    dhcp.subnets.push({
      ip: '172.31.255.0',
      leasetime: defaultSubnet.leasetime,
      mask: '255.255.255.0',
      options: defaultSubnet.options,
      pool_end: defaultSubnet.pool_end,
      pool_start: defaultSubnet.pool_start,
    });

    // add vlan auto deploy for all switch connected
    const allNodeConnected = getAllNodeConnected(nodeId, edges, nodes);
    const allSwitchConnected = allNodeConnected.filter(
      (node) =>
        (node.data?.category as NodeCategoryModel).type === EDataType.Switch,
    );

    allSwitchConnected.forEach((switchNode) => {
      const edgeConnected = getEdgeConnected(nodeId, switchNode.id, edges);
      if (edgeConnected) {
        const portIndex = getPortIndexConnected(
          nodeId,
          edgeConnected,
          nodeCategory,
        );

        if (portIndex) {
          const name = autoDeployIps.find(
            (ip) => ip.id === switchNode.id,
          )?.name;
          dhcp.lease.push({
            ip: autoDeployIps.find((ip) => ip.id === switchNode.id)?.ip || '',
            options: [
              {
                code: 125,
                type: 4,
                value: [
                  `http://${autoDeployIps.find((ip) => ip.id === nodeId)?.ip}/files/files.tar`,
                  `${name}.cfg`,
                ],
              },
            ],
            type: 0,
            value: [3579, portIndex - 1],
          });
        }
      }
    });
  }

  return dhcp;
};

function getDhcpSubnet(
  subnet: string,
  subnetMask: string,
  dhcpServer?: IDhcpServerSettings,
): IConfigurationSubnet {
  const options: IConfigurationSubnet['options'] = [];

  if (
    isValidIPAddress(dhcpServer?.routerIp1) ||
    isValidIPAddress(dhcpServer?.routerIp2)
  ) {
    const routerValues = [dhcpServer?.routerIp1, dhcpServer?.routerIp2].filter(
      (ip): ip is string => isValidIPAddress(ip),
    );
    options.push({ code: 3, type: 0, value: routerValues });
  }

  if (
    isValidIPAddress(dhcpServer?.domainServerIp1) ||
    isValidIPAddress(dhcpServer?.domainServerIp2)
  ) {
    const dnsValues = [
      dhcpServer?.domainServerIp1,
      dhcpServer?.domainServerIp2,
    ].filter((ip): ip is string => isValidIPAddress(ip));
    options.push({ code: 6, type: 0, value: dnsValues });
  }

  if (dhcpServer?.domainName) {
    options.push({
      code: 15,
      type: 0,
      value: [dhcpServer.domainName],
    });
  }

  if (
    isValidIPAddress(dhcpServer?.ntpServerIp1) ||
    isValidIPAddress(dhcpServer?.ntpServerIp2)
  ) {
    const ntpValues = [
      dhcpServer?.ntpServerIp1,
      dhcpServer?.ntpServerIp2,
    ].filter((ip): ip is string => isValidIPAddress(ip));
    options.push({ code: 42, type: 0, value: ntpValues });
  }

  let pool_end = '0.0.0.0';
  let pool_start = '0.0.0.0';
  if (dhcpServer?.poolEnabled) {
    if (isValidIPAddress(dhcpServer?.poolEnd)) {
      pool_end = dhcpServer?.poolEnd || '0.0.0.0';
    }
    if (isValidIPAddress(dhcpServer?.poolStart)) {
      pool_start = dhcpServer?.poolStart || '0.0.0.0';
    }
  }

  return {
    ip: subnet || '',
    leasetime: dhcpServer?.leaseTime || 86400,
    mask: subnetMask || '',
    options,
    pool_end,
    pool_start,
  };
}
